import {controller} from '@github/catalyst'
import type {ErrorContext} from '@github-ui/failbot'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {ReactPartialAnchorElement} from '@github-ui/react-partial-anchor-element'
import {BrowserRouter, Route, Routes} from '@github-ui/react-router'

import type {EmbeddedPartialData} from './embedded-data-types'
import {PartialEntry} from './PartialEntry'
import {PartialRouter} from './PartialRouter'
import {Profiler, ProfilerProvider} from './ProfilerContext'
import {getPartialAnchorProps} from './react-partial-anchor'
import {getReactPartial} from './react-partial-registry'
import {ReactBaseElement} from './ReactBaseElement'

// What is this silliness? Is it react or a web component?!
// It's a web component we use to bootstrap react partials within the monolith.
@controller
class ReactPartialElement extends ReactBaseElement<EmbeddedPartialData> {
  nameAttribute = 'partial-name'

  async getReactNode(embeddedData: EmbeddedPartialData, onError: (error: Error, context?: ErrorContext) => void) {
    const {Component} = await getReactPartial(this.name)

    // Some React Partials will be wrapped in a react-partial-anchor, which is used to conditionally render the Partial
    const anchorElement = this.closest<ReactPartialAnchorElement>('react-partial-anchor')

    const partialAnchorProps = getPartialAnchorProps(anchorElement)

    const mergedEmbeddedData = {
      ...embeddedData,
      props: {
        ...embeddedData.props,
        ...partialAnchorProps,
      },
    }
    const isReactCustomPartialRouterEnabled = isFeatureEnabled('react_custom_partial_router')
    const routeContent = (
      <Routes>
        <Route path="*" element={<Component {...mergedEmbeddedData.props} />} />
      </Routes>
    )

    return (
      <ProfilerProvider appName={this.name} isDataRouterEnabled={false}>
        <Profiler id={this.name}>
          <PartialEntry
            partialName={this.name}
            wasServerRendered={this.hasSSRContent}
            onError={onError}
            ssrError={this.ssrError}
          >
            {isReactCustomPartialRouterEnabled ? (
              <PartialRouter partialName={this.name}>{routeContent}</PartialRouter>
            ) : (
              <BrowserRouter>{routeContent}</BrowserRouter>
            )}
          </PartialEntry>
        </Profiler>
      </ProfilerProvider>
    )
  }
}
